import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Select,
  Button,
  Space,
  Typography,
  Modal,
  Input,
  message,
  Tag,
  Divider,
  Row,
  Col
} from 'antd';
import {
  PrinterOutlined,
  PlusOutlined,
  DisconnectOutlined,
  ApiOutlined,
  WifiOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useOnboard } from '../pages/Onboard/OnboardContext';
import { useAuth } from '../contexts/AuthContext';
import printerService, { enviarparaimpressora } from '../services/printerService';
import { isAndroid } from '../utils/androidUtils';
import wsPrintClient from '../services/wsPrintClient';
import './PrinterSelection.css';

const { Text } = Typography;
const { Option } = Select;

const normalizePrinterType = (type) => {
  const value = String(type || '').trim().toLowerCase();
  if (value === 'websocket') return 'socket';
  if (value === 'bluetooth' || value === 'socket' || value === 'webbluetooth') return value;
  return '';
};

const PrinterSelection = ({
  onPrinterConnect,
  onPrinterDisconnect,
  showTitle = true,
  size = 'default'
}) => {
  const { user } = useAuth();
  const {
    printerName,
    setPrinterName,
    printerType,
    setPrinterType,
    printerDevice,
    setPrinterDevice,
    printerConnected,
    setPrinterConnected,
    printerEmulator,
    setPrinterEmulator
  } = useOnboard();
  const normalizedPrinterType = normalizePrinterType(printerType);

  const [ipModalVisible, setIpModalVisible] = useState(false);
  const [uuid, setUuid] = useState('');
  const [loading, setLoading] = useState(false);
  const [pairedWith, setPairedWith] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [webBluetoothSupported, setWebBluetoothSupported] = useState(false);
  const [webBluetoothSecureContext, setWebBluetoothSecureContext] = useState(false);
  const [webBluetoothStatus, setWebBluetoothStatus] = useState('Desconectado');
  const uuidInputRef = useRef(null);

  // Gera/obtém UUID do cliente para pareamento
  const getClientUUID = () => {
    try {
      const key = 'clientUUID';
      let current = localStorage.getItem(key);
      if (!current) {
        current = (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2, 10));
        localStorage.setItem(key, current);
      }
      return current;
    } catch {
      return Math.random().toString(36).slice(2, 10);
    }
  };

  // Inicializar callbacks do printer service
  useEffect(() => {
    printerService.setCallbacks({
      onConnect: (connectionInfo) => {
        setPrinterConnected(true);
        if (connectionInfo.type === 'bluetooth') {
          setPrinterName(connectionInfo.device || null);
          setPrinterDevice(connectionInfo.device || null);
          setPrinterEmulator('Bluetooth');
        } else if (connectionInfo.type === 'webbluetooth') {
          setPrinterName(connectionInfo.device || null);
          setPrinterDevice(connectionInfo.device || null);
          setPrinterEmulator('Web Bluetooth');
          setWebBluetoothStatus(`Conectado: ${connectionInfo.device}`);
        } else if (connectionInfo.type === 'websocket') {
          setPrinterName(connectionInfo.device);
          setPrinterDevice(connectionInfo.device);
          setPrinterEmulator('WebSocket');
        }
        message.success('Impressora conectada com sucesso!');
        try {
          const a = new Audio('/sound/printer_ok.mp3');
          a.play().catch(() => { });
        } catch { void 0 }
        if (onPrinterConnect) {
          onPrinterConnect(connectionInfo);
        }
      },
      onDisconnect: () => {
        setPrinterConnected(false);
        setPrinterName(null);
        setPrinterDevice(null);
        setPrinterEmulator(null);
        setWebBluetoothStatus('Desconectado');
        message.info('Impressora desconectada');
        if (onPrinterDisconnect) {
          onPrinterDisconnect();
        }
      },
      onError: (error) => {
        setWebBluetoothStatus(error || 'Falha na conexío BLE');
        message.error(error);
        setLoading(false);
      },
      onDeviceList: (devices) => {
        const list = Array.isArray(devices) ? devices : [];
        message.success(`${list.length} dispositivos encontrados`);
      }
    });
    return () => {
      try { printerService.setCallbacks({}); } catch (error) { void error; }
      try { wsPrintClient.setCallbacks({}); } catch (error) { void error; }
    };
  }, [onPrinterConnect, onPrinterDisconnect, setPrinterConnected, setPrinterName, setPrinterDevice, setPrinterEmulator]);

  // Callbacks do cliente WS-Print
  useEffect(() => {
    wsPrintClient.setCallbacks({
      onOpen: ({ url }) => {
        void url;
        setPairedWith(null);
      },
      onClose: () => {
        setPrinterConnected(false);
        setPrinterName(null);
        setPrinterDevice(null);
        setPrinterEmulator(null);
        setPairedWith(null);
        if (onPrinterDisconnect) onPrinterDisconnect();
      },
      onError: (error) => {
        message.error(error?.message || 'Erro no WS-Print');
        setLoading(false);
      },
      onStatus: (st) => {
        // Tratamento de pareamento

        if (Object.prototype.hasOwnProperty.call(st || {}, 'pairedWith')) {
          if (st.pairedWith) {
            // Validação: Ignora se o UUID atual não corresponde ao da tentativa de conexío
            if (wsPrintClient.ident?.pairWithUuid && String(uuid) !== String(wsPrintClient.ident.pairWithUuid)) {
              return;
            }

            setPairedWith(st.pairedWith);
            message.success(`Selecione uma impressora em: ${st.pairedWith}`);
          } else {
            // Evento de recusa de pareamento (no_paredo/no_pareado)
            setPairedWith(null);
            setPrinterConnected(false);
            setPrinterName(null);
            setPrinterDevice(null);
            setPrinterEmulator(null);
            message.warning('Não pareou com o dispositivo');
          }
        }
      },
      onPrinter: (nome) => {
        if (nome != null && String(nome).trim() !== '') {
          setPrinterConnected(true);
          setPrinterName(nome);
          setPrinterDevice(pairedWith || uuid || null);
          setPrinterType('socket');
          setPrinterEmulator('WebSocket');
          if (onPrinterConnect) {
            onPrinterConnect({ type: 'websocket', device: pairedWith || uuid || null, name: nome, status: 'connected' });
            try {
              const a = new Audio('/sound/printer_ok.mp3');
              a.play().catch(() => { });
            } catch { void 0 }
          }
        }
      },
      onPrinters: (list) => {
        const arr = Array.isArray(list) ? list : [];
        setPrinters(arr);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, pairedWith]);

  // Função para listar dispositivos Bluetooth
  const listaBluetooth = async () => {
    try {
      setLoading(true);
      await printerService.listBluetoothDevices();
    } catch (error) {
      void error;
    } finally {
      setLoading(false);
    }
  };

  // Função para listar WebSocket
  const listaWebSocket = () => {
    setIpModalVisible(true);
  };

  useEffect(() => {
    setWebBluetoothSupported(printerService.isWebBluetoothSupported());
    setWebBluetoothSecureContext(printerService.isWebBluetoothSecureContext());
  }, []);

  useEffect(() => {
    if (ipModalVisible) {
      setTimeout(() => {
        try { uuidInputRef.current?.focus(); } catch (error) { void error; }
      }, 100);
    }
  }, [ipModalVisible]);

  const conectarWebBluetooth = async () => {
    if (!webBluetoothSupported) {
      setWebBluetoothStatus('Web Bluetooth indisponível neste navegador');
      message.warning('Seu navegador não suporta Web Bluetooth. Use Chrome ou Edge no Windows.');
      return;
    }

    if (!webBluetoothSecureContext) {
      setWebBluetoothStatus('Web Bluetooth requer HTTPS ou localhost');
      message.warning('Web Bluetooth exige HTTPS ou localhost para funcionar.');
      return;
    }

    try {
      setLoading(true);
      setWebBluetoothStatus('Solicitando permissío...');
      await printerService.connectWebBluetooth();
      setPrinterType('webbluetooth');
    } catch (error) {
      const msg = error?.message || 'Falha ao conectar via Web Bluetooth';
      setWebBluetoothStatus(msg);
    } finally {
      setLoading(false);
    }
  };

  // Função para selecionar tipo de impressora
  const selecionaTipoImpressora = async (tipo = normalizedPrinterType) => {
    const t = normalizePrinterType(tipo);
    switch (t) {
      case 'bluetooth':
        if (isAndroid()) {
          await listaBluetooth();
        } else {
          message.info('No desktop, use a opção Web Bluetooth (BLE).');
        }
        break;
      case 'webbluetooth':
        await conectarWebBluetooth();
        break;
      case 'socket':
        listaWebSocket();
        break;
      default:
        message.warning('Selecione um tipo de impressora');
    }
  };

  // Função para conectar via WebSocket
  const conectarWebSocket = async () => {
    // Compatível com a regra fornecida: iniciar WS e depois parear
    const idNumerico = parseInt(uuid, 10);
    if (!(idNumerico >= 999 && idNumerico <= 10000)) {
      message.error('Por favor, insira o ID do App Etiquetando Print');
      return;
    }

    try {
      setLoading(true);
      try { setPrinterConnected(false); } catch (error) { void error; }
      try { setPrinterName(''); } catch (error) { void error; }
      try { setPairedWith(''); } catch (error) { void error; }
      const clientUUID = getClientUUID();

      // Inicializa conexío com headers e uuid do cliente e já solicita pareamento
      await wsPrintClient.initializeWebsocket({
        id: clientUUID,
        branches: { sigla: user?.assignedBranch?.sigla || user?.assignedBranch?.acronym || '' },
        server: false,
        computer: window.navigator.userAgent,
        codigo: user?.codigo || 'on board',
        name: user?.name || 'on board',
        emparelhar: true,
        versao: 'on board',
        pairWithUuid: String(uuid)
      });

      await new Promise((resolve) => setTimeout(resolve, 5000));

      if (!wsPrintClient.pairedWith) {
        try { wsPrintClient.websocket?.close(); } catch (error) { void error; }
        setPrinterConnected(false);
        setPrinterName(null);
        setPrinterDevice(null);
        setPrinterEmulator(null);
        setPairedWith(null);
        setUuid('');
        setIpModalVisible(false);
        message.warning('Pareamento não realizado. Verifique o ID e tente novamente.');
        desconectar();
      }
    } catch (error) {
      detectarErroConexao(error);
    } finally {
      setLoading(false);
    }
  };

  const detectarErroConexao = (error) => {
    console.error('Erro na conexío WebSocket:', error);
    message.error('Não foi possível conectar. Verifique se o aplicativo de impressão está rodando.');
  };

  // Função para desconectar impressora
  const desconectar = async () => {
    try {
      setLoading(true);
      await printerService.disconnect();
    } catch (error) {
      message.error('Erro ao desconectar impressora');
      console.error('Erro na desconexío:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para cadastrar impressora (manutenção)
  const handlePrinterMaintenance = () => {
    message.info('Funcionalidade de cadastro em desenvolvimento');
  };

  const handleTestPrint = async () => {
    try {
      setLoading(true);
      const zplCode = `^XA
^MUD
^JMA
^PW800
^LL480
^LS0
^LT0
^CI28
^FO0,0^GB800,480,2^FS
^FO045,020^GB65,35,20,,3^FS
^FO060,025^A0,35,25^FR^FD$filial^FS
^FO200,025^AD,25,10^FDDTE: $data $prazo^FS
^FO045,085^A0,30,19^FDNF:^FS
^FO095,060^A0,100,100^FD$nf^FS
^FO100,320^BY3^BCN,80,N,N,N^FD>;$barcode128^FS
^FO380,150^BQN,2,4^FDQA,$barcode128^FS
^XZ`;

      await enviarparaimpressora(zplCode);
      message.success('Código de teste enviado para impressora com sucesso!');
    } catch (error) {
      message.error(`Erro ao enviar código de teste: ${error.message}`);
      console.error('Erro ao imprimir teste:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="printer-selection">
      <Card
        size={size}
        title={showTitle ? (
          <Space>
            <PrinterOutlined />
            <Text strong>Impressora</Text>
          </Space>
        ) : null}
        style={{ marginBottom: 'clamp(12px, 2.5vw, 16px)' }}
      >
        <Row gutter={[8, 8]} align="middle">
          <Col span={16}>
            <Select
              value={normalizedPrinterType || undefined}
              onChange={(value) => {
                const normalizedValue = normalizePrinterType(value);
                setPrinterType(normalizedValue);
                if (normalizedValue === 'webbluetooth') {
                  setWebBluetoothStatus(
                    !webBluetoothSupported
                      ? 'Chrome/Edge no Windows necessário'
                      : (!webBluetoothSecureContext ? 'HTTPS ou localhost obrigatório' : 'Clique em Conectar para abrir o seletor do navegador')
                  );
                  return;
                }
                selecionaTipoImpressora(normalizedValue);
              }}
              placeholder="Selecione o tipo"
              style={{ width: '100%', fontSize: 'clamp(12px, 3vw, 14px)' }}
              size={size}
            >
              <Option value="bluetooth">
                <Space>
                  <ApiOutlined />
                  Bluetooth
                </Space>
              </Option>
              <Option value="webbluetooth">
                <Space>
                  <ApiOutlined />
                  Web Bluetooth (BLE)
                </Space>
              </Option>
              <Option value="socket">
                <Space>
                  <WifiOutlined />
                  Etiquetando Print
                </Space>
              </Option>
            </Select>
          </Col>
          <Col span={8}>
            <Space>
              {!printerConnected ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => selecionaTipoImpressora(normalizedPrinterType)}
                  loading={loading}
                  size={size}
                  disabled={!normalizedPrinterType}
                  style={{ width: '130%', fontSize: 'clamp(10px, 2.5vw, 12px)', height: 'clamp(28px, 6vw, 36px)', padding: '0 clamp(8px, 3vw, 12px)' }}
                >
                  <span className="ps-connect-label">Conectar</span>
                </Button>
              ) : (
                <Button
                  type="primary"
                  danger
                  icon={<DisconnectOutlined />}
                  onClick={desconectar}
                  size={size}
                  style={{ width: '150%', fontSize: 'clamp(12px, 3vw, 14px)', height: 'clamp(28px, 6vw, 36px)', padding: '0 clamp(8px, 3vw, 12px)' }}
                >
                  Desconectar
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        {normalizedPrinterType === 'webbluetooth' && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 'clamp(11px, 2.3vw, 13px)' }}>
              {webBluetoothStatus}. Clique no botío Conectar.
            </Text>
          </div>
        )}



        {/* Status da impressora */}
        {(printerName || printerDevice || printerEmulator) && (
          <>
            <Divider style={{ margin: 'clamp(8px, 2.5vw, 12px) 0' }} />
            <div className="ps-status">
              {printerName && (
                <Tag color="blue" icon={<PrinterOutlined />} style={{ fontSize: 'clamp(10px, 2.5vw, 13px)' }}>
                  {printerName}
                </Tag>
              )}
              {printerDevice && (
                <Tag color="green" style={{ fontSize: 'clamp(10px, 2.5vw, 13px)' }}>
                  ID: {printerDevice}
                </Tag>
              )}
              {printerEmulator && (
                <Tag color="purple" style={{ fontSize: 'clamp(10px, 2.5vw, 13px)' }}>
                  {printerEmulator}
                </Tag>
              )}
              {pairedWith && (
                <Tag color="gold" style={{ fontSize: 'clamp(10px, 2.5vw, 13px)' }}>
                  Pareado com: {pairedWith}
                </Tag>
              )}
              {printerConnected && (
                <>
                  <Button
                    type="text"
                    icon={<PrinterOutlined />}
                    onClick={handleTestPrint}
                    size="small"
                    loading={loading}
                    style={{ fontSize: 'clamp(10px, 2vw, 12px)', padding: 'clamp(2px, 0.6vw, 4px) clamp(6px, 2vw, 8px)' }}
                  >
                    Teste
                  </Button>
                  <Button
                    type="text"
                    icon={<SettingOutlined />}
                    onClick={handlePrinterMaintenance}
                    size="small"
                    style={{ fontSize: 'clamp(10px, 2vw, 12px)', padding: 'clamp(2px, 0.6vw, 4px) clamp(6px, 2vw, 8px)' }}
                  >
                    Cadastrar
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </Card>

      {/* Modal para inserir ID do WebSocket */}
      <Modal
        title="Inserir I.D da Impressora"
        open={ipModalVisible}
        maskClosable={false}
        onCancel={() => {
          setIpModalVisible(false);
          setUuid('');
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIpModalVisible(false);
              setUuid('');
            }}
          >
            Cancelar
          </Button>,
        ]}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Row gutter={[8, 8]} justify="center" align="middle">
            <Col span={14}>
              <Input
                type="number"
                id="uuid"
                value={uuid}
                ref={uuidInputRef}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 4);
                  setUuid(value);
                }}
                onPressEnter={conectarWebSocket}
                placeholder="0000"
                min={0}
                max={9999}
                className="ps-modal-input"
                style={{ textAlign: 'center' }}
                maxLength={4}
              />
            </Col>
            <Col span={10}>
              <Button
                key="connect"
                type="primary"
                loading={loading}
                onClick={conectarWebSocket}
              >
                Conectar
              </Button>
            </Col>
          </Row>
          <div style={{ marginTop: 8, color: '#666' }}>
            <Text type="secondary" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>Digite o ID de 4 dígitos</Text>
          </div>

          <div style={{ marginTop: 12 }}>
            <Select
              placeholder="Selecione a impressora no Etiquetando Print"
              style={{ width: '100%' }}
              value={printerName || undefined}
              onChange={(value) => {
                try { wsPrintClient.emit({ impressora: value, uuid }); } catch (error) { void error; }
              }}
              options={printers.map((p) => ({ label: p, value: p }))}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PrinterSelection;
